import React, { useState, useEffect, useRef } from "react";

import { pencilIcon } from "@excalidraw/excalidraw/components/icons";

import { useAtomValue } from "../app-jotai";
import { currentCustomFileAtom } from "../app-jotai";
import { CustomFileStorage } from "../data/customFileStorage";
import { renameFile } from "../data/customFileApi";

import "./FileNameEditor.scss";

export const FileNameEditor: React.FC = () => {
  const currentFile = useAtomValue(currentCustomFileAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(currentFile.name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edited name when current file changes
  useEffect(() => {
    setEditedName(currentFile.name);
  }, [currentFile.name]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== currentFile.name) {
      if (currentFile.id) {
        try {
          // Use PATCH (name-only) so this never overwrites drawing elements
          await renameFile(currentFile.id, trimmedName);
          // Update local tracking to reflect new name
          CustomFileStorage.setCurrentFile({
            ...currentFile,
            name: trimmedName,
          });
        } catch (error) {
          console.error("Failed to save file name change:", error);
          setEditedName(currentFile.name);
        }
      } else {
        // File not yet saved to backend — just update local tracking;
        // the next autosave will pick up the new name
        CustomFileStorage.setCurrentFile({
          ...currentFile,
          name: trimmedName,
        });
      }
    } else {
      // Revert to current name if empty or unchanged
      setEditedName(currentFile.name);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(currentFile.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  return (
    <div className="FileNameEditor">
      <div className="FileNameEditor__icon">{pencilIcon}</div>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          className="FileNameEditor__input"
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={100}
        />
      ) : (
        <button
          className="FileNameEditor__display"
          onClick={handleStartEdit}
          title="Click to edit file name"
        >
          {currentFile.name || "Untitled"}
        </button>
      )}
    </div>
  );
};
